import { useState, useEffect, useCallback } from "react";
import { useWindowSize } from "Providers/WindowSizeProvider";
import { useApp } from "Providers/AppProvider";
import { useEnabledWorkingDaysLength } from "Components/CompanySetting/CompanySettingHelper";
import useLocalStorageState from "use-local-storage-state/dist";
import {
	IsCalendarSubHeadOpen,
	LocalStorageKey,
	SetIsCalendarSubHeadOpen,
} from "Helpers/LocalStorageHelper";
import {
	ScheduleGroup,
	ScheduleEntryType,
	ScheduleEntryTypeCategory,
} from "domain-entities";
import { useTheme, useMediaQuery } from "@material-ui/core";
import { useSchedule } from "Components/Schedule/hooks/useSchedule";
import { useCalendar } from "Components/Common/hooks/useCalendar";
import { MinuteRange } from "Components/WorkingHours/types/MinuteRange";

export interface CellSize {
	navigation: {
		appHeight: number;
		calendarHeight: number;
	};
	offset: {
		top: number;
		left: number;
	};
	month: {
		nameWidth: number;
		dayWidth: number;
		weeksLength: number;
	};
	week: {
		nameWidth: number;
		dayWidth: number;
	};
	day: {
		nameWidth: number;
		hourWidth: number;
	};
}

const MIN_MONTH_DAY_WIDTH = 26;
const MAX_MONTH_WEEKS_LENGTH = 8;
const MAX_NAME_WIDTH = 400;

function getHoursArray(workingMinutes: MinuteRange) {
	const startHour = Math.floor(workingMinutes.start / 60);
	const endHour = Math.ceil(workingMinutes.end / 60);
	return Array(endHour - startHour)
		.fill(null)
		.map((_, index) => startHour + index);
}

export function useCellSize(): CellSize {
	const { mountPoint, scrollElement, isStandaloneApp } = useApp();
	const theme = useTheme();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
	const windowSize = useWindowSize();
	const daysLength = useEnabledWorkingDaysLength();
	const workingMinutes = useCalendar((state) => state.workingMinutes);
	const hours = getHoursArray(workingMinutes);
	const [offsetLeft, setOffsetLeft] = useState(0);
	const [offsetTop, setOffsetTop] = useState(0);
	const [weekNameWidth, setWeekNameWidth] = useState(0);
	const [weekDayWidth, setWeekDayWidth] = useState(0);
	const [dayNameWidth, setDayNameWidth] = useState(0);
	const [dayHourWidth, setDayHourWidth] = useState(0);
	const [monthNameWidth, setMonthNameWidth] = useState(0);
	const [monthDayWidth, setMonthDayWidth] = useState(0);
	const [monthWeeksLength, setMonthWeeksLength] = useState(0);

	const appRect = mountPoint?.getBoundingClientRect();
	const appRectLeft = appRect?.left || 0;
	const appRectTop = appRect?.top || 0;
	const scrollLeft = isStandaloneApp ? scrollElement?.scrollLeft || 0 : 0;
	const scrollTop = isStandaloneApp ? scrollElement?.scrollTop || 0 : 0;

	useEffect(() => {
		// offset
		const nextOffsetLeft = scrollLeft + appRectLeft;
		const appWidth = windowSize.width - nextOffsetLeft;

		setOffsetLeft(nextOffsetLeft);
		setOffsetTop(scrollTop + appRectTop);

		// week
		const daysWidth = Math.max(appWidth * 0.75, appWidth - MAX_NAME_WIDTH);
		const nextWeekDayWidth = Math.round(daysWidth / daysLength);

		setWeekDayWidth(nextWeekDayWidth);
		setWeekNameWidth(appWidth - nextWeekDayWidth * daysLength);

		// day
		const hoursLength = hours.length;
		const hoursWidth = Math.max(appWidth * 0.75, appWidth - MAX_NAME_WIDTH);
		const nextDayHourWidth = Math.round(hoursWidth / hoursLength);

		setDayHourWidth(nextDayHourWidth);
		setDayNameWidth(appWidth - nextDayHourWidth * hoursLength);

		// month
		const maxMonthDaysWidth = MIN_MONTH_DAY_WIDTH * daysLength;
		const nextMonthWeeksLength = Math.min(
			Math.floor(daysWidth / maxMonthDaysWidth),
			MAX_MONTH_WEEKS_LENGTH
		);

		const nextMonthDayWidth = Math.ceil(
			daysWidth / nextMonthWeeksLength / daysLength
		);

		setMonthWeeksLength(nextMonthWeeksLength);
		setMonthDayWidth(nextMonthDayWidth);
		setMonthNameWidth(
			appWidth - nextMonthDayWidth * daysLength * nextMonthWeeksLength
		);
	}, [
		windowSize.width,
		appRectLeft,
		appRectTop,
		scrollLeft,
		scrollTop,
		daysLength,
		hours.length,
	]);

	return {
		navigation: {
			appHeight: 60,
			calendarHeight: isSmallScreen ? 48 : 74,
		},
		offset: {
			top: offsetTop,
			left: offsetLeft,
		},
		month: {
			nameWidth: monthNameWidth,
			dayWidth: monthDayWidth,
			weeksLength: monthWeeksLength,
		},
		week: {
			nameWidth: weekNameWidth,
			dayWidth: weekDayWidth,
		},
		day: {
			nameWidth: dayNameWidth,
			hourWidth: dayHourWidth,
		},
	};
}

export function useIsCalendarSubHeadOpen() {
	const scheduleGroups = useSchedule((state) => state.item?.groups);

	const [isOpen, setIsOpen] = useLocalStorageState<IsCalendarSubHeadOpen>(
		LocalStorageKey.IS_CALENDAR_SUB_HEAD_OPEN,
		{
			schedulePhases: true,
			employees: true,
			resources: true,
		}
	);

	(scheduleGroups || []).forEach((scheduleGroup) => {
		if (isOpen[scheduleGroup.id] === undefined) {
			isOpen[scheduleGroup.id] = true;
		}
	});

	const setIsCalendarSubHeadOpen = useCallback<SetIsCalendarSubHeadOpen>(
		(scheduleGroupId: string, nextIsOpen: boolean) => {
			isOpen[scheduleGroupId] = nextIsOpen;
			setIsOpen({ ...isOpen });
		},
		[isOpen, setIsOpen]
	);

	return {
		isCalendarSubHeadOpen: isOpen,
		setIsCalendarSubHeadOpen,
	};
}

export function getSplittedScheduleGroupIds(scheduleGroups?: ScheduleGroup[]) {
	const scheduleGroupMemberIds: string[] = [];
	const scheduleGroupResourceIds: string[] = [];

	if (scheduleGroups) {
		scheduleGroups.forEach((scheduleGroup) => {
			scheduleGroup.items.forEach((scheduleGroupItem) => {
				if (scheduleGroupItem.userId) {
					scheduleGroupMemberIds.push(scheduleGroupItem.userId);
				} else if (scheduleGroupItem.resourceId) {
					scheduleGroupResourceIds.push(scheduleGroupItem.resourceId);
				}
			});
		});
	}

	return {
		scheduleGroupMemberIds,
		scheduleGroupResourceIds,
	};
}

export function getSplittedScheduleEntryTypeIds(
	scheduleEntryTypes: ScheduleEntryType[]
) {
	const memberScheduleEntryTypeIds = scheduleEntryTypes
		.filter((item) => item.category === ScheduleEntryTypeCategory.USER)
		.map((item) => item.id);

	const resourceScheduleEntryTypeIds = scheduleEntryTypes
		.filter((item) => item.category === ScheduleEntryTypeCategory.RESOURCE)
		.map((item) => item.id);

	return {
		memberScheduleEntryTypeIds,
		resourceScheduleEntryTypeIds,
	};
}
